import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import type { Notification, NotificationList } from "@/lib/notifications";
import { playNotificationChime, primeNotificationSound } from "@/lib/notificationSound";

/**
 * One poller for the whole app: the bell badge, the dropdown list and the
 * slide-in popups all read from here, so a logged-in buyer / BL Advisor / admin
 * costs a single request per cycle instead of one per surface.
 *
 * Arrival detection is id-based rather than timestamp-based: anything unread we
 * have not shown before pops up and chimes. Signing in starts with an empty
 * record, so everything still unread pops immediately on login.
 */

const POLL_MS = 30_000;
/** Popups allowed on screen at once; the rest stay in the bell. */
const MAX_VISIBLE_POPUPS = 5;

const SOUND_KEY = "bl.notifications.sound";
/** Every per-session key this feature writes shares this prefix, so sign-out can
 *  wipe them in one sweep (see clearSessionKeys). The sound preference lives in
 *  localStorage and is deliberately not covered. */
export const NOTIF_SESSION_PREFIX = "bl.notifications.";
/** Per-tab record of what we've already popped, so reloading a page does not
 *  replay the same messages. Cleared on sign-out so the next login pops. */
const ANNOUNCED_KEY = `${NOTIF_SESSION_PREFIX}announced`;
/** Trim the remembered ids - only recent ones can still be unread. */
const ANNOUNCED_MAX = 200;

type NotificationsContextValue = {
  items: Notification[];
  unread: number;
  loading: boolean;
  refresh: () => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  popups: Notification[];
  dismissPopup: (id: string) => void;
  clearPopups: () => void;
  soundOn: boolean;
  toggleSound: () => void;
};

const NotificationsContext = createContext<NotificationsContextValue>({
  items: [],
  unread: 0,
  loading: false,
  refresh: () => {},
  markRead: () => {},
  markAllRead: () => {},
  popups: [],
  dismissPopup: () => {},
  clearPopups: () => {},
  soundOn: true,
  toggleSound: () => {},
});

export function useNotifications() {
  return useContext(NotificationsContext);
}

function readSoundPref(): boolean {
  try {
    return window.localStorage.getItem(SOUND_KEY) !== "off";
  } catch {
    return true;
  }
}

function readAnnounced(userId: string): Set<string> {
  try {
    const raw = window.sessionStorage.getItem(`${ANNOUNCED_KEY}.${userId}`);
    return new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set<string>();
  }
}

function writeAnnounced(userId: string, ids: Set<string>): void {
  try {
    const trimmed = Array.from(ids).slice(-ANNOUNCED_MAX);
    window.sessionStorage.setItem(`${ANNOUNCED_KEY}.${userId}`, JSON.stringify(trimmed));
  } catch {
    /* private mode / quota - worst case a reload re-announces */
  }
}

/** Wipe every stored record so the next sign-in pops all unread afresh — both
 *  the announced ids and the login digest's "already shown" flag. */
function clearSessionKeys(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const k = window.sessionStorage.key(i);
      if (k && k.startsWith(NOTIF_SESSION_PREFIX)) doomed.push(k);
    }
    doomed.forEach((k) => window.sessionStorage.removeItem(k));
  } catch {
    /* nothing to clear */
  }
}

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [popups, setPopups] = useState<Notification[]>([]);
  const [soundOn, setSoundOn] = useState(readSoundPref);

  // Ids we've already announced. A ref (not state) so the poll callback never
  // goes stale and re-announces what the user has already seen.
  const announced = useRef<Set<string>>(new Set());
  const soundRef = useRef(soundOn);
  soundRef.current = soundOn;

  useEffect(() => primeNotificationSound(), []);

  const ingest = useCallback((list: Notification[], userId: string) => {
    const fresh = list.filter((n) => !n.is_read && !announced.current.has(n.id));
    list.forEach((n) => announced.current.add(n.id));
    writeAnnounced(userId, announced.current);
    if (fresh.length === 0) return;

    // The API returns newest first; keep that order in the stack.
    setPopups((prev) => [...fresh, ...prev].slice(0, MAX_VISIBLE_POPUPS));
    if (soundRef.current) playNotificationChime();
  }, []);

  const userId = user?.id ?? null;
  // The session a response belongs to must still be the live one when it
  // lands. A poll in flight at sign-out / session timeout (the focus refresh
  // fires exactly when you return to an idle tab) used to resolve after the
  // reset below had emptied `announced`, so every unread popped up again on
  // top of the sign-in page.
  const liveUserId = useRef(userId);
  liveUserId.current = userId;

  const refresh = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    api
      .get<NotificationList>("/notifications")
      .then((d) => {
        if (liveUserId.current !== userId) return;
        setItems(d.items);
        setUnread(d.unread_count);
        ingest(d.items, userId);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [ingest, userId]);

  // Poll while signed in; reset on sign-out so the next account starts clean
  // instead of inheriting the previous user's announced ids.
  useEffect(() => {
    if (!userId) {
      announced.current = new Set();
      clearSessionKeys();
      setItems([]);
      setUnread(0);
      setPopups([]);
      return;
    }
    // A reload mid-session must not replay popups the user already saw; a real
    // sign-in finds an empty record (cleared above on logout) and pops them all.
    announced.current = readAnnounced(userId);
    refresh();
    const id = window.setInterval(refresh, POLL_MS);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [userId, refresh]);

  const dismissPopup = useCallback((id: string) => {
    setPopups((prev) => prev.filter((p) => p.id !== id));
  }, []);

  /** Drop every queued slide-in without marking anything read. The login digest
   *  uses this so a buyer isn't shown the same unread twice at once. */
  const clearPopups = useCallback(() => setPopups([]), []);

  const markRead = useCallback((id: string) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    setUnread((c) => Math.max(0, c - 1));
    setPopups((prev) => prev.filter((p) => p.id !== id));
    api.patch(`/notifications/${id}/read`, {}).catch(() => {});
  }, []);

  const markAllRead = useCallback(() => {
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnread(0);
    setPopups([]);
    api.patch("/notifications/mark-all-read", {}).catch(() => {});
  }, []);

  const toggleSound = useCallback(() => {
    setSoundOn((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(SOUND_KEY, next ? "on" : "off");
      } catch {
        /* private mode - the preference just won't persist */
      }
      return next;
    });
  }, []);

  const value = useMemo<NotificationsContextValue>(
    () => ({
      items,
      unread,
      loading,
      refresh,
      markRead,
      markAllRead,
      popups,
      dismissPopup,
      clearPopups,
      soundOn,
      toggleSound,
    }),
    [items, unread, loading, refresh, markRead, markAllRead, popups, dismissPopup, clearPopups, soundOn, toggleSound]
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}
